/**
 * What an import into a CLOUD workspace sends to `POST /collections/import`,
 * and how many calls that takes.
 *
 * Pure on purpose: the hook that calls this reaches the stores, the toasts and
 * the network; the payload shape and the request count are the parts worth
 * pinning with tests (src/tests/cloudImportPayload.test.js).
 *
 * ## One call per import, split only by size
 *
 * The server writes a whole call in one transaction and a constant number of
 * SQL statements (backend `Collections.ImportWriter`), so the only reason to
 * make a second call is the request-body cap: Phoenix parses JSON bodies up to
 * 8,000,000 bytes and answers 413 above that. Collections are packed into
 * batches under IMPORT_BATCH_BUDGET_BYTES, whole collections only — a
 * collection is never split across calls, because each call creates the
 * collections it carries. For scale: a 1.9 MB Swagger file with 184 operations
 * is a ~230 KB body, so a real import is one call.
 */
import { prepareRequestBodyForApi } from './requestPayload';

/** Phoenix's JSON body cap is 8,000,000 bytes; stay well under it. */
export const IMPORT_BATCH_BUDGET_BYTES = 6_000_000;

/** One normalized collection (collectionImport/types.js) → its import payload. */
export function toCloudImportCollection(col) {
  return {
    name: col.name,
    description: col.description || '',
    settings: col.settings || {},
    requests: (col.requests || []).map((req) => prepareRequestBodyForApi(req, null, req.name)),
  };
}

/** UTF-8 size of the JSON the client will send for `value`. */
export function jsonByteLength(value) {
  const json = JSON.stringify(value);
  if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(json).length;
  return unescape(encodeURIComponent(json)).length;
}

/**
 * Pack payload collections into as few calls as the body cap allows, in file
 * order. A single collection larger than the budget still gets its own batch —
 * the server's 413 then says so, rather than the import silently dropping it.
 *
 * @param {object[]} collections payload collections (toCloudImportCollection)
 * @param {number} [budget]
 * @returns {object[][]} batches, each the `collections` array of one call
 */
export function planCloudImportBatches(collections, budget = IMPORT_BATCH_BUDGET_BYTES) {
  const batches = [];
  let current = [];
  let size = 0;
  for (const col of collections || []) {
    const colSize = jsonByteLength(col) + 1;
    if (current.length > 0 && size + colSize > budget) {
      batches.push(current);
      current = [];
      size = 0;
    }
    current.push(col);
    size += colSize;
  }
  if (current.length > 0) batches.push(current);
  return batches;
}
